import React, {PropsWithChildren} from 'react';
import {useQuery} from '@tanstack/react-query';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import * as utc from 'dayjs/plugin/utc';
import {ShortReservation, Housing} from '@guestapp/sdk';
import {useChekinSDK} from './ChekinSDK';
import {QUERY_CACHE_KEYS} from '../utils/constants';

type ContextProps = {
  data?: ShortReservation;
  housing?: Housing;
  error?: Error | null;
  isLoading?: boolean;
  isFetching?: boolean;
  isLoadingError?: boolean;
  housingId?: string;
  isBiometricMatchForAll?: boolean;
  isSuccessLoadedHousing?: boolean;
};

enum GUESTAPP_V1_DOMAINS {
  staging = 'new-chekin.stg.chekin.com',
  production = 'guestapp.chekin.com',
}

const ReservationContext = React.createContext<ContextProps>({
  data: undefined,
  housing: undefined,
  isLoading: false,
  isFetching: false,
});

function ReservationProvider({children}: PropsWithChildren) {
  const {getReservation, reservationId, isSuccessSDK, getHousing} = useChekinSDK();
  dayjs.extend(utc.default);
  dayjs.extend(isBetween);
  const {
    data: reservation,
    error,
    isLoading,
    isFetching,
    isLoadingError,
  } = useQuery<ShortReservation, Error>(
    [QUERY_CACHE_KEYS.reservation],
    () => getReservation(reservationId || ''),
    {
      initialData: {} as ShortReservation,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      enabled: Boolean(reservationId) && isSuccessSDK,
      onSuccess: data => {
        if (
          data?.signup_form_link.includes(GUESTAPP_V1_DOMAINS.staging) ||
          data?.signup_form_link.includes(GUESTAPP_V1_DOMAINS.production)
        ) {
          window.location.href = data?.signup_form_link;
        }
      },
    },
  );
  const housingId = reservation.housing_id;

  const {
    data: housing,
    // error: errorHousing,
    // isLoading: isLoadingHousing,
    isSuccess: isSuccessLoadedHousing,
  } = useQuery<Housing, Error>(
    [QUERY_CACHE_KEYS.housing],
    () => getHousing(housingId || ''),
    {
      initialData: {} as Housing,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      enabled: Boolean(housingId),
    },
  );

  const isBiometricMatchForAll = housing.is_biometric_match_for_all_enabled;

  const defaultProps = React.useMemo<ContextProps>(
    () => ({
      data: reservation,
      housing,
      housingId,
      error,
      isLoading,
      isFetching,
      isLoadingError,
      isBiometricMatchForAll,
      isSuccessLoadedHousing,
    }),
    [
      error,
      housing,
      housingId,
      isBiometricMatchForAll,
      isFetching,
      isLoading,
      isLoadingError,
      reservation,
      isSuccessLoadedHousing,
    ],
  );

  return <ReservationContext.Provider value={defaultProps} children={children} />;
}

function useReservation() {
  const context = React.useContext(ReservationContext);
  if (context === undefined) {
    throw new Error('useReservation must be used within a ReservationProvider');
  }
  return context;
}

export {ReservationContext, ReservationProvider, useReservation};
